import { type HttpClient, segment } from '../http';

import {
  type CreateTaxonomyValuePayload,
  type MergeResult,
  type TaxonomyGroup,
  type TaxonomyValue,
  type UpdateTaxonomyValuePayload,
} from './taxonomies.types';

/**
 * Editing what the catalogue can say about a game.
 *
 * Warning: administration routes. An application session is refused on all of
 * them, and every write is recorded in the audit log — merging or deleting a
 * value touches sheets that belong to other people.
 */
export function createTaxonomiesApi(http: HttpClient) {
  return {
    /** The five taxonomies, with the usage that decides what may be done. */
    list: () => http.get<TaxonomyGroup[]>('/admin/taxonomies'),

    /**
     * Adds a value.
     *
     * ⚠️ The key is fixed here for good. The screen proposes it from the
     * English label and locks it the moment the value is saved.
     */
    create: (kind: string, payload: CreateTaxonomyValuePayload) =>
      http.post<TaxonomyValue>(`/admin/taxonomies/${segment(kind)}`, payload),

    /** ⚠️ Labels only, and replaced rather than merged. */
    update: (kind: string, key: string, payload: UpdateTaxonomyValuePayload) =>
      http.patch<TaxonomyValue>(
        `/admin/taxonomies/${segment(kind)}/${segment(key)}`,
        payload,
      ),

    /** ⚠️ Refused the moment one game carries the value. */
    remove: (kind: string, key: string) =>
      http.delete<void>(`/admin/taxonomies/${segment(kind)}/${segment(key)}`),

    /**
     * Absorbs one value into another, moving the games with it.
     *
     * @returns The value kept, and the games that moved — the only record of
     * what a merge would have to undo.
     */
    merge: (kind: string, key: string, intoKey: string) =>
      http.post<MergeResult>(
        `/admin/taxonomies/${segment(kind)}/${segment(key)}/merge`,
        { intoKey },
      ),
  };
}
